import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import {
  PATH_RESOLVABLE_SERVICE_QUOTATION_INFO,
  PATH_RESOLVABLE_SERVICE_QUOTATION_INFO_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableServiceForm,
  ResolvableServiceFormTab,
} from '../../forms/ResolvableService';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ResolvableAmount } from '../../components/ResolvableFields/ResolvableAmount';
import { ResolvablePercent } from '../../components/ResolvableFields/ResolvablePercent';
import { ResolvableId } from '../../components/ResolvableFields/ResolvableId';
import { ConfidentialValue } from '../../components/ConfidentialValue';
import { N_A } from '../../constants/labels.constant';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import type { ResolvableFieldSettings } from '../../components/ResolvableFields/types/resolvable-field-settings.type';

export const ResolvableServicePage_QuotationInfo_ResolvableServiceBaseInterface_Fragment =
  graphql(`
    fragment ResolvableServicePage_QuotationInfo_ResolvableServiceBaseInterface_Fragment on ResolvableServiceBaseInterface {
      _id
      value {
        priceExcludingVat {
          ...ResolvableAmountCmp_ResolvableAmount_Fragment
        }
        vatId {
          ...ResolvableIdCmp_ResolvableId_Fragment
        }
        discountPercentage {
          ...ResolvablePercentCmp_ResolvablePercent_Fragment
        }
        discountAmount {
          ...ResolvableAmountCmp_ResolvableAmount_Fragment
        }
      }
      ...ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment
    }
  `);

const DEFAULT_RESOLVABLE_SETTINGS = {
  enableDirectGeneration: false,
  primaryMode: ResolvableModeEnum.Static,
};

interface Props {
  readonly resolvableService: FragmentType<
    typeof ResolvableServicePage_QuotationInfo_ResolvableServiceBaseInterface_Fragment
  >;
  readonly resolvableFieldSettings?: Partial<ResolvableFieldSettings>;
}

export const QuotationInfo = ({
  resolvableService: pResolvableService,
  resolvableFieldSettings: {
    enableDirectGeneration = false,
    primaryMode = ResolvableModeEnum.Static,
  } = DEFAULT_RESOLVABLE_SETTINGS,
}: Readonly<Props>) => {
  const resolvableFieldSettings = {
    enableDirectGeneration,
    primaryMode,
  };

  const resolvableService = getFragmentData(
    ResolvableServicePage_QuotationInfo_ResolvableServiceBaseInterface_Fragment,
    pResolvableService,
  );

  const { vatsIdMap } = useReferencesContext();

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_SERVICE_QUOTATION_INFO,
      edit: PATH_RESOLVABLE_SERVICE_QUOTATION_INFO_EDIT,
    },
    { id: resolvableService._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { value } = resolvableService;

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Informations Devis
        </Typography>
        <Paper sx={{ p: 2 }}>
          {value ? (
            <Box sx={{ position: 'relative' }}>
              <Typography color="info.main" gutterBottom variant="body1">
                Prix Hors Taxe :{' '}
                {/* eslint-disable-next-line react/jsx-max-depth */}
                <ResolvableAmount
                  resolvableAmount={value.priceExcludingVat}
                  resolvableFieldSettings={resolvableFieldSettings}
                />
              </Typography>
              <Typography color="info.main" gutterBottom variant="body1">
                TVA : {/* eslint-disable-next-line react/jsx-max-depth */}
                <ResolvableId
                  idMap={vatsIdMap}
                  resolvableFieldSettings={resolvableFieldSettings}
                  resolvableId={value.vatId}
                />
              </Typography>
              <Typography color="info.main" gutterBottom variant="body1">
                Montant de Remise :{' '}
                {!isDefined(value.discountAmount) ? (
                  N_A
                ) : (
                  <ResolvableAmount
                    resolvableAmount={value.discountAmount}
                    resolvableFieldSettings={resolvableFieldSettings}
                  />
                )}
              </Typography>
              <Typography color="info.main" gutterBottom variant="body1">
                Pourcentage de Remise :{' '}
                {!isDefined(value.discountPercentage) ? (
                  N_A
                ) : (
                  <ResolvablePercent
                    resolvableFieldSettings={resolvableFieldSettings}
                    resolvablePercent={value.discountPercentage}
                  />
                )}
              </Typography>
            </Box>
          ) : (
            <ConfidentialValue />
          )}
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editResolvableServiceQuotationInfo',
            icon: <EditIcon />,
            name: 'Modifier Infos Devis',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations de Devis"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableServiceForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableService}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableServiceId={resolvableService._id}
          stepIdsSubset={[
            ResolvableServiceFormTab.ResolvableServiceQuotationInfo,
          ]}
          title="Formulaire de modification des Informations de Devis"
        />
      </RightDrawer>
    </>
  );
};
